import React from "react";
import BreadCrumb from "@/components/global/BreadCrumb";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation } from "next-i18next";
import { fetchHandshake } from "@/services/handshake";
import Seo from "@/components/seo";
import { useRouter } from "next/router";
import { getOrderTrackingDetails } from "@/services/user/orders";
import Link from "next/link";
import EmptyGuard from "@/components/global/Empty";
import Image from "@/components/global/Image";

const TrackOrder = ({ order }) => {
  const { t } = useTranslation("success");
  const { locale } = useRouter();

  const BreadCrumbItems = [
    {
      name: locale == "en" ? "Track Order" : "تتبع الطلب",
      url: "/track-order",
      active: true,
      key: "track-order",
    },
  ];

  console.log("order d", order);
  return (
    <>
      <Seo title={locale == "en" ? "Track Order" : "تتبع الطلب"} />
      <BreadCrumb items={BreadCrumbItems} />
      <EmptyGuard
        title={
          order?.message == "Invalid Params"
            ? t("Sorry , You dont have any orders with this tracking number")
            : order?.message
        }
        data={order?.success === false ? [] : [order]}
        description={
          order?.message == "Invalid Params"
            ? t(
                "Sorry but the order you are looking for does not exist, please make sure you entered the correct tracking number and email address then try again."
              )
            : ""
        }
        icon={
          <Image
            width={200}
            height={200}
            alt={""}
            src={"/imgs/no-orders-found.svg"}
            className="mb-7"
          />
        }
      >
        <div className="grid w-11/12 grid-cols-12 gap-5 mx-auto my-4 sm:my-8">
          <h1 className="font-bold col-span-full sm:text-2xl">
            {locale == "en" ? "Track Order" : "تتبع الطلب"}
          </h1>
          <div className="col-span-full text-center mb-8">
            <p className="font-semibold sm:text-xl">
              {t("Tracking number...")}
            </p>
            <p className="mt-5 font-bold sm:text-3xl text-[#5559A0]">
              {order?.awb_tracking_numbers}
            </p>
            <p className="mt-5 font-semibold sm:text-xl text-[#5559A0]">
              {t("Shipping Company")}:{" "}
              <span className="font-bold">{t(order?.shipping_method)}</span>
            </p>
          </div>
          <div className="col-span-full">
            <div className="col-span-full">
              <div className="overflow-x-auto mb-11">
                <table className="min-w-full table-auto border-collapse rounded-lg">
                  <thead className="bg-[#545AA1] text-white">
                    <tr>
                      <th className="px-4 py-2 text-start">{t("Location")}</th>
                      <th className="px-4 py-2 text-start">{t("Date")}</th>
                      <th className="px-4 py-2 text-start">
                        {t("Local Time")}
                      </th>
                      <th className="px-4 py-2 text-start">
                        {t("Shipment Status")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {order?.aramex_details?.map((state, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-2">{state?.location}</td>
                        <td className="px-4 py-2">{state?.date}</td>
                        <td className="px-4 py-2">{state?.time}</td>
                        <td className="px-4 py-2">{state?.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="col-span-full lg:col-span-4">
            <div className="*:mb-4">
              <h2 className="bg-zinc-100 p-2 font-bold rounded-sm text-lg">
                {t("Order Information")}
              </h2>
              <div className="flex items-center gap-3">
                <span className="min-w-32">{t("Order Date")} : </span>
                <span className="font-bold">{order?.order_date}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="min-w-32">{t("Shipment Status")} : </span>
                <span className="font-bold">{order?.status || "unknown"}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="min-w-32">{t("Order number")} : </span>
                <span className="font-bold">
                  #{order?.increment_id || "unknown"}
                </span>
              </div>
            </div>
          </div>
          <div className="col-span-full lg:col-span-4">
            <div className="*:mb-2">
              <h2 className="bg-zinc-100 p-2 font-bold rounded-sm text-lg">
                {t("Billing Address")}
              </h2>
              <p>{order?.billing_address?.at(0)?.name || "unknown"}</p>
              <p>{order?.billing_address?.at(0)?.telephone || ""}</p>
              <p>
                {order?.billing_address?.at(0)?.street},{" "}
                {order?.billing_address?.at(0)?.city},{" "}
                {order?.billing_address?.at(0)?.country}
              </p>
            </div>
          </div>
          <div className="col-span-full lg:col-span-4">
            <div className="*:mb-2">
              <h2 className="bg-zinc-100 p-2 font-bold rounded-sm text-lg">
                {t("Shipping Address")}
              </h2>
              <p>{order?.shipping_address?.at(0)?.name || "unknown"}</p>
              <p>{order?.shipping_address?.at(0)?.telephone || ""}</p>
              <p>
                {order?.shipping_address?.at(0)?.street},{" "}
                {order?.shipping_address?.at(0)?.city},{" "}
                {order?.shipping_address?.at(0)?.country}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4 flex-wrap col-span-full">
            <Link
              href="/"
              className="w-full sm:w-60 bg-primary text-white p-3 rounded-sm font-bold text-center"
            >
              {t("Continue Shopping")}
            </Link>

            <button className="w-full sm:w-60 border border-secondary-600 text-secondary-800 font-bold p-3 rounded-sm">
              {t("Print Order")}
            </button>
          </div>
        </div>
      </EmptyGuard>
    </>
  );
};

export default TrackOrder;

export async function getServerSideProps({ locale, req, res, query }) {
  // get handshake from cookies or generate new handshake
  const handshake = await fetchHandshake({ req, res, locale });

  // get ordert tracking data
  const order = await getOrderTrackingDetails(
    handshake,
    query?.order_number,
    query?.email
  );

  // if (order?.data?.error || !order?.data?.data) {
  //   return {
  //     notFound: true,
  //   };
  // }

  return {
    props: {
      order: order?.data?.data || {},
      ...(await serverSideTranslations(locale, ["common", "success"])),
    },
  };
}
